import React from 'react';

import Image from '@/core/components/Image';
import { useFF } from '@/core/lib/ff/ff.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { FeatureFlag } from '@/core/types/featureFlag.type';

type AvatarSize = '16' | '24' | '32' | '40' | '48' | '52' | '80' | '100';

interface AvatarProps {
  avatarUrl?: string;
  size: AvatarSize;
  isSuperUser?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({ avatarUrl, size, isSuperUser }) => {
  const { t } = useTranslationContext(['common']);
  const { isFFactivate: hasFFSuperUser } = useFF(FeatureFlag.SUPER_USER);

  if (!avatarUrl) return null;

  const sizes = {
    16: 'h-4 w-4',
    24: 'h-6 w-6',
    32: 'h-8 w-8',
    40: 'h-10 w-10',
    48: 'h-12 w-12',
    52: 'h-13 w-13',
    80: 'h-20 w-20',
    100: 'size-12 min-w-12 lg:size-25 lg:min-w-25',
  } satisfies { [key in AvatarSize]: string };

  const superUserClassName = {
    16: '',
    24: '',
    32: '',
    40: 'size-4',
    48: '',
    80: '',
    100: 'size-[18px] lg:size-[30px]',
    52: 'size-[18px]',
  }[size];

  return (
    <div
      className={`relative flex items-center justify-center rounded-full ${sizes[size]} border-[3px] ${hasFFSuperUser && isSuperUser && superUserClassName ? 'border-[#E89F2F]' : 'border-transparent'}`}
    >
      <Image
        loading="lazy"
        decoding="async"
        className="rounded-full"
        src={avatarUrl.replace('{{size}}', Number(size) > 32 ? '3' : '1').replace('{{ext}}', 'webp')}
        type="image/webp"
        fallback={avatarUrl.replace('{{size}}', Number(size) > 32 ? '3' : '1').replace('{{ext}}', 'jpg')}
        fallbackType="image/jpeg"
        alt={t('components.avatar.alt', { ns: 'common' })}
        width={size}
        height={size}
      />
      {hasFFSuperUser && isSuperUser && superUserClassName && (
        <div className="absolute -bottom-[3px] -right-[3px]">
          <IconSuperUser className={superUserClassName} />
        </div>
      )}
    </div>
  );
};

export default Avatar;

const IconSuperUser: React.FC<{ className: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_18190_20453)">
      <g clipPath="url(#clip1_18190_20453)">
        <path
          d="M14.4297 1.31055C14.7198 0.952663 15.2802 0.952663 15.5703 1.31055C16.4546 2.4009 18.0134 2.66776 19.2139 1.94629C19.622 1.70124 20.1407 1.9028 20.2842 2.32227C20.7403 3.65468 22.1189 4.42902 23.4922 4.15723C23.9657 4.06347 24.3726 4.42828 24.3623 4.86035C24.3287 6.27216 25.3614 7.47029 26.748 7.67871C27.22 7.74956 27.4727 8.22008 27.3135 8.62207C26.7931 9.93609 27.3477 11.4184 28.5859 12.0859C28.9925 12.3051 29.0776 12.8238 28.7822 13.1553C27.8428 14.2085 27.8428 15.7915 28.7822 16.8447C29.0776 17.1762 28.9925 17.6949 28.5859 17.9141C27.3477 18.5816 26.7931 20.0639 27.3135 21.3779C27.4727 21.7799 27.22 22.2504 26.748 22.3213C25.3614 22.5297 24.3287 23.7278 24.3623 25.1396C24.3726 25.5717 23.9657 25.9365 23.4922 25.8428C22.1189 25.571 20.7403 26.3453 20.2842 27.6777C20.1407 28.0972 19.622 28.2988 19.2139 28.0537C18.0134 27.3322 16.4546 27.5991 15.5703 28.6895C15.2802 29.0473 14.7198 29.0473 14.4297 28.6895C13.5454 27.5991 11.9866 27.3322 10.7861 28.0537C10.378 28.2988 9.85934 28.0972 9.71582 27.6777C9.25967 26.3453 7.88109 25.571 6.50781 25.8428C6.03428 25.9365 5.6274 25.5717 5.6377 25.1396C5.6713 23.7278 4.63864 22.5297 3.25195 22.3213C2.78004 22.2504 2.52728 21.7799 2.68652 21.3779C3.20686 20.0639 2.65227 18.5816 1.41406 17.9141C1.00754 17.6949 0.922373 17.1762 1.21777 16.8447C2.15717 15.7915 2.15718 14.2085 1.21777 13.1553C0.922372 12.8238 1.00754 12.3051 1.41406 12.0859C2.65226 11.4184 3.20686 9.93609 2.68652 8.62207C2.52728 8.22009 2.78004 7.74956 3.25195 7.67871C4.63864 7.47029 5.6713 6.27216 5.6377 4.86035C5.6274 4.42827 6.03427 4.06347 6.50781 4.15723C7.8811 4.42902 9.25967 3.65468 9.71582 2.32227C9.85934 1.90279 10.378 1.70124 10.7861 1.94629C11.9866 2.66776 13.5454 2.4009 14.4297 1.31055Z"
          fill="#FFAF34"
          stroke="#FFAF34"
          strokeWidth="2.08333"
        />
        <g clipPath="url(#clip2_18190_20453)">
          <path
            d="M14.8555 17C17.6169 17 19.8555 14.7614 19.8555 12C19.8555 9.23858 17.6169 7 14.8555 7C12.094 7 9.85547 9.23858 9.85547 12C9.85547 14.7614 12.094 17 14.8555 17Z"
            stroke="white"
            strokeWidth="1.42857"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12.1494 16.2073L11.2852 22.7145L14.8566 20.5716L18.428 22.7145L17.5637 16.2002" stroke="white" strokeWidth="1.42857" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    </g>
    <defs>
      <clipPath id="clip0_18190_20453">
        <rect width="30" height="30" fill="white" />
      </clipPath>
      <clipPath id="clip1_18190_20453">
        <rect width="154.286" height="154.286" fill="white" transform="translate(-62.1445 -62.1426)" />
      </clipPath>
      <clipPath id="clip2_18190_20453">
        <rect width="17.1429" height="17.1429" fill="white" transform="translate(6.28516 6.28613)" />
      </clipPath>
    </defs>
  </svg>
);
